import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { enUS } from "date-fns/locale";
import { DateRange } from "react-date-range";

import "./DateRangePicker.scss";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { Spinner } from "reactstrap";
import { dateTimeFormat } from "../../utils/dateFormatUtils";

import closeIcon from "../../assets/images/app/common/aou-close-icon.svg";
import calendarIcon from "../../assets/images/app/common/calendar-icon.svg";

const DateRangePicker = ({
  range,
  onChange,
  disabled = false,
  onClear = () => {},
  showClearIcon = false,
  hasUserSelectedDate = false,
  isLoading = false,
  minDate,
  maxDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const isDateSelected = !!range?.[0]?.startDate || !!range?.[0]?.endDate;

  // Default min/max dates spanning 10 years before and after current date
  const defaultMinDate = moment()
    .subtract(10, "years")
    .startOf("year")
    .toDate(); // 10 years ago, start of year
  const defaultMaxDate = moment().add(10, "years").endOf("year").toDate(); // 10 years from now, end of year

  const finalMinDate = minDate || defaultMinDate;
  const finalMaxDate = maxDate || defaultMaxDate;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formatDateRange = (range) => {
    if (!range?.[0]) return "";

    const startDate = range[0]?.startDate
      ? moment(range[0].startDate).format(dateTimeFormat.americanFormat)
      : "";
    const endDate = range[0]?.endDate
      ? moment(range[0].endDate).format(dateTimeFormat.americanFormat)
      : "";

    return startDate && endDate
      ? `${startDate} - ${endDate}`
      : startDate || endDate || "Select Date Range";
  };

  return (
    <div className="date-range-wrapper align-items-center" ref={wrapperRef}>
      <div
        className={`input-container ${
          disabled ? "input-container-disabled" : "input-container-enabled"
        }`}
      >
        <input
          type="text"
          readOnly
          placeholder={isLoading ? "Loading" : "Select Date Range"}
          value={
            isLoading
              ? "Select Date Range"
              : hasUserSelectedDate
              ? formatDateRange(range)
              : ""
          }
          className="input-field"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          loading={isLoading}
        />
        <div className="input-icon-wrapper">
          {isLoading ? (
            <Spinner className="input-icon" color="primary" />
          ) : (
            <>
              {showClearIcon && isDateSelected && hasUserSelectedDate && (
                <img
                  src={closeIcon}
                  alt="Clear Date"
                  className="input-close-icon"
                  onClick={() => !disabled && onClear(setIsOpen)}
                />
              )}
              <img
                src={calendarIcon}
                alt="Calendar Icon"
                className="input-icon"
                onClick={() => !disabled && setIsOpen(!isOpen)}
              />
            </>
          )}
        </div>
      </div>

      {isOpen && (
        <DateRange
          ranges={range}
          onChange={onChange}
          rangeColors={isDateSelected ? ["#58af54"] : ["#ffffff"]}
          months={1}
          showSelectionPreview={true}
          locale={enUS}
          minDate={finalMinDate}
          maxDate={finalMaxDate}
          showDateDisplay={false}
          showMonthArrow={true}
          direction={"vertical"}
          showMonthAndYearPickers={true}
          className={`date-range-picker mobile`}
        />
      )}
    </div>
  );
};

export default DateRangePicker;
